// Befehle eines Abschnitts, dessen Datensatz jemand anderes haelt -- die Seite
// (Name, Status im Kontaktkopf) oder useMaintainedSection (Kennungen,
// Kontaktwege, Adressen).
//
// Der Hook haelt nur, was ein Befehl hinterlaesst: EINE Meldung, die
// Bestaetigung, die Feldfehler, busy und ob das Anlegen ohne Antwort blieb.
// Den Datensatz laedt er nicht. Den neuen Stand reicht der Aufrufer weiter:
// aus der Antwort (`onApplied`) oder aus dem stillen Nachladen (`refresh`).
//
// DIE REGELN, die sonst jeder Abschnitt neu lernen musste:
//   * Der Fokus geht nach JEDEM Befehl auf die Statusregion -- in einem Effekt
//     NACH dem Rerender. Im `then` gesetzt traefe er einen Knopf, den React
//     gleich entfernt, und fiele auf das Dokument zurueck (WCAG 2.4.3).
//     Ausnahme Feldablehnung: dann geht er auf das erste markierte Feld im
//     Bereich (`sectionRef`), denn dort ist der Fehler zu beheben (WCAG 3.3.1).
//     Die Region sagt die Ablehnung trotzdem an; nur ohne markiertes Feld
//     bekommt sie den Fokus.
//   * Das Anlegen haelt seine Absendekennung, solange eine Wiederholung etwas
//     aendern kann; "Erneut versuchen" sendet dieselbe (ADR-0060). Die Eingabe
//     wird erst NACH dem Erfolg geleert -- vorher geleert vernichtete jede
//     Ablehnung sie (NFR-UX-005).
//   * Nach einem Konflikt oder 404 wird still nachgeladen, damit sichtbar ist,
//     was JETZT gilt. Scheitert das Nachladen, sagt die Statusregion das --
//     sonst behauptete sie einen Stand, den der Bildschirm nicht zeigt
//     (NFR-UX-005).
//   * Bricht das Anlegen ab, wird im PROBEMODUS nachgeladen: nur ein Erfolg
//     darf die Anzeige aendern, sonst verschwaende die stehende Eingabe samt
//     "Erneut versuchen".
//   * Ein Befehl ohne Rueckfrage (etwa Beenden, ADR-0061 Ziffer 17) darf nach
//     einem Abbruch "Erneut versuchen" anbieten (`retryAfterAbort`): ohne
//     Dialog gibt es keinen Ort, an dem die Entscheidung stehen bliebe, und
//     die Wiederholung traegt denselben Stand (lock_version).

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';

import type { PlainMessage } from '../access.ts';
import { CLIENT_REQUEST_SUPERSEDED, keepsRequest, requestFor, TRIM_ONLY, type HeldRequest, type InputNormalization } from '../client-request.ts';
import type { CommandOutcome, FieldError } from '../command-client.ts';

/** Laden und stilles Nachladen ohne Antwort: geschrieben wird dabei nichts. */
export const UNREACHABLE_MESSAGE: PlainMessage = Object.freeze({
  headline: 'LIAISON ist gerade nicht erreichbar.',
  explanation: 'Der aktuelle Stand ließ sich nicht laden.',
  nextStep: 'Prüfe deine Verbindung und versuche es erneut.',
  retryable: true,
});

/**
 * Ein Befehl ohne Antwort: ob er gewirkt hat, weiss der Browser nicht. Die
 * Seite, nicht der Abschnitt, laedt neu -- nicht jeder Abschnitt hat dafuer
 * einen Knopf, und der Satz verweist auf keinen, den es nicht gibt. Wer es
 * genauer weiss, gibt `unreachable` mit.
 */
/**
 * Ein Konflikt, und das Nachladen danach scheitert: abgelehnt ist sicher, nur
 * der neue Stand fehlt. Kein Lesesatz -- er verschwiege die Ablehnung -- und
 * kein "Erneut versuchen", das mit dem alten Stand wieder abgelehnt wuerde.
 */
export function conflictNotReloadedMessage(keepsInput: boolean): PlainMessage {
  return Object.freeze({
    headline: 'Deine Änderung wurde nicht gespeichert.',
    explanation: 'Jemand anderes hat hier inzwischen etwas geändert. Der neue Stand ließ sich gerade nicht laden.',
    nextStep: keepsInput ? 'Lade die Seite neu und prüfe den Stand. Deine Eingabe steht noch im Formular.' : 'Lade die Seite neu und prüfe den Stand.',
    retryable: false,
  });
}

export const COMMAND_UNCERTAIN_MESSAGE: PlainMessage = Object.freeze({
  headline: 'Ob die Änderung gespeichert wurde, ist offen.',
  explanation: 'Die Antwort kam nicht an.',
  nextStep: 'Lade die Seite neu. Dann siehst du, was gilt.',
  retryable: true,
});

export interface SectionCommandsOptions {
  readonly accessToken: () => Promise<string | undefined>;
  readonly onReauthenticate: () => void;
  /**
   * Holt still den jetzt gueltigen Stand und reicht ihn weiter -- nach einem
   * Erfolg (ausser `reload: false`), nach Konflikt oder 404. `probe`: nach
   * einem abgebrochenen Anlegen; nur ein Erfolg darf die Anzeige aendern.
   * Ein zurueckgegebener Satz ersetzt die Meldung; scheitert das Nachladen,
   * steht UNREACHABLE_MESSAGE da (nicht im Probemodus). `false`: nicht geladen,
   * eine Meldung hat der Aufrufer schon gesetzt (im Probemodus keine). Darf je
   * Rerender neu entstehen.
   */
  readonly refresh: (probe: boolean) => Promise<PlainMessage | false | void>;
  /** Die Saetze des Fachmoduls zum Anlegen (ADR-0060); nur noetig, wer `create` ruft. */
  readonly createMessages?: CreateMessages;
  /** Die Eingabe bleibt nach einem Konflikt im Formular stehen; der Satz darf es dann sagen. */
  readonly keepsInputOnConflict?: boolean;
}

/** Die Saetze zum Anlegen (ADR-0060). */
export interface CreateMessages {
  /** Das Absenden blieb ohne Antwort; die Wiederholung ist sicher. */
  readonly submitFailed: PlainMessage;
}

export interface CommandSpec<Applied> {
  readonly run: (token: string) => Promise<CommandOutcome<Applied>>;
  /** Die Bestaetigung nennt die WIRKUNG (CM-UX-004). */
  readonly confirm: (applied: Applied) => string;
  /** Nach einem Erfolg, vor dem Nachladen -- etwa den neuen Stand an die Seite geben oder eine Bestaetigung schliessen. */
  readonly onApplied?: (applied: Applied) => void;
  /** `false`: die Antwort traegt den ganzen neuen Stand, `onApplied` gibt ihn weiter; ein zweiter Abruf entfaellt. */
  readonly reload?: boolean;
  /** Die Ueberschrift der Bestaetigung, wo "Gespeichert" die Wirkung nicht trifft -- etwa "Die Beziehung ist beendet." */
  readonly confirmTitle?: string;
  /** Der Satz, wenn die Antwort ausbleibt; sonst COMMAND_UNCERTAIN_MESSAGE. */
  readonly unreachable?: PlainMessage;
  /**
   * Nach einem Abbruch still nachladen, damit der Abschnitt zeigt, was gilt --
   * wo eine Wiederholung mit dem alten Stand ins Leere liefe. `reloaded` steht
   * da, wenn das Nachladen gelang, sonst `notReloaded`. Ersetzt `unreachable`.
   * Als gelungen gilt nur ein `refresh`, der im Probemodus `undefined` liefert;
   * jeder Satz, den er zurueckgibt, zaehlt als nicht geladen.
   */
  readonly reloadAfterAbort?: { readonly reloaded: PlainMessage; readonly notReloaded: PlainMessage };
  /**
   * Bleibt die Antwort aus, steht "Erneut versuchen" da und sendet DENSELBEN
   * Befehl. Nur, wo eine Wiederholung sicher ist: derselbe Stand, und ein
   * schon geschriebenes Ergebnis erkennt der Befehl selbst.
   */
  readonly retryAfterAbort?: boolean;
  /**
   * Der zugaengliche Name von "Erneut versuchen", wo dieselbe Handlung an
   * mehreren Zeilen steht -- etwa "Erneut versuchen: Beziehung „…“ beenden".
   * Er beginnt mit dem sichtbaren Text (WCAG 2.5.3).
   */
  readonly retryLabel?: string;
}

export interface CreateSpec<Applied> {
  /** Die Eingabe, wie sie abgeschickt wird; gleicher Inhalt heisst gleiche Absendekennung. */
  readonly input: Readonly<Record<string, string>>;
  readonly normalization?: InputNormalization;
  readonly run: (token: string, clientRequestId: string) => Promise<CommandOutcome<Applied>>;
  readonly confirm: (applied: Applied) => string;
  /** Die Eingabe leeren -- nach einem Erfolg und wenn der erste Versuch schon angekommen war. */
  readonly clearInput: () => void;
  readonly onApplied?: (applied: Applied) => void;
  /**
   * Eigene Saetze, wo dieser Aufruf etwas anderes anlegt als das Formular des
   * Abschnitts -- etwa einen weiteren Zweck an einem vorhandenen Eintrag. Sonst
   * gelten `createMessages`.
   */
  readonly messages?: CreateMessages;
  /**
   * Auch nach einer Feldablehnung still nachladen: wo die Felder aus dem
   * geladenen Stand waehlen (etwa die Lesarten einer Beziehung), kann die
   * Ablehnung heissen, dass es die Auswahl nicht mehr gibt.
   */
  readonly refreshOnInvalid?: boolean;
}

export interface SectionCommands {
  readonly message: PlainMessage | undefined;
  /**
   * Ob die Meldung einen OFFENEN Ausgang nennt (Antwort fehlt). Dann ist sie
   * eine Warnung; eine Ablehnung oder ein gescheitertes Laden ohne offenen
   * Ausgang ist ein Fehler (meldungsIntent).
   */
  readonly messageOffen: boolean;
  readonly confirmation: string | undefined;
  /** Die Ueberschrift der Bestaetigung, falls der Befehl eine eigene mitgab. */
  readonly confirmationTitle: string | undefined;
  readonly fieldErrors: readonly FieldError[];
  readonly busy: boolean;
  /** Das Anlegen blieb ohne Antwort oder scheiterte an einer Stoerung -- dann steht "Erneut versuchen" da. */
  readonly submitFailed: boolean;
  /** Ein Befehl mit `retryAfterAbort` blieb ohne Antwort: "Erneut versuchen" sendet ihn noch einmal. Sonst undefined. */
  readonly retryCommand: (() => void) | undefined;
  /** Der zugaengliche Name dazu, falls der Befehl einen mitgab (`retryLabel`). */
  readonly retryLabel: string | undefined;
  /** Das Ziel der Fokusfuehrung; gehoert an die EINE Region mit aria-live. */
  readonly statusRef: RefObject<HTMLDivElement | null>;
  /** Wo nach einer Feldablehnung das erste markierte Feld gesucht wird; ohne ihn bleibt der Fokus bei der Statusregion. */
  readonly sectionRef: RefObject<HTMLDivElement | null>;
  readonly command: <Applied>(spec: CommandSpec<Applied>) => Promise<void>;
  readonly create: <Applied>(spec: CreateSpec<Applied>) => Promise<void>;
  /** Eine Meldung, die nicht aus einem Befehl kommt -- etwa ein gescheitertes Laden. */
  readonly showMessage: (message: PlainMessage | undefined) => void;
  /** Eine Bestaetigung, die nicht aus einem Befehl kommt -- etwa nach einem ausdruecklichen Neuladen. */
  readonly showConfirmation: (text: string, title: string) => void;
  /** Meldung und Bestaetigung weg -- vor einem ausdruecklichen Neuladen. */
  readonly clearMessages: () => void;
  /**
   * Alles weg, was der letzte Befehl hinterliess, auch Feldfehler und ein
   * offenes "Erneut versuchen" -- wenn eine andere Handlung beginnt (etwa eine
   * Suche). Sonst stuende der Knopf ohne seinen Satz da. Eine gehaltene
   * Absendekennung bleibt: dieselbe Eingabe traegt weiter dieselbe.
   */
  readonly clearOutcome: () => void;
  /** Fokus auf die Statusregion, nach dem naechsten Rerender. */
  readonly focusStatus: () => void;
}

/**
 * Das erste als ungueltig markierte Feld in Dokumentreihenfolge. Traegt eine
 * Gruppe die Markierung (Fluent setzt sie an die Radiogruppe), bekommt ihre
 * gewaehlte Option den Fokus, sonst die erste.
 */
function erstesMarkiertesFeld(bereich: HTMLElement | null): HTMLElement | undefined {
  const markiert = bereich?.querySelector<HTMLElement>('[aria-invalid="true"]') ?? undefined;
  if (markiert === undefined || markiert.getAttribute('role') !== 'radiogroup') {
    return markiert;
  }
  return (
    markiert.querySelector<HTMLElement>('input[type="radio"]:checked:not(:disabled)') ??
    markiert.querySelector<HTMLElement>('input[type="radio"]:not(:disabled)') ??
    undefined
  );
}

/**
 * Die Art der Leiste zu einer Meldung: ein Hinweis
 * ("bereits so") informiert, ein offener Ausgang warnt, eine Ablehnung von
 * LIAISON oder ein gescheitertes Laden ohne offenen Ausgang ist ein Fehler.
 * Fluent sagt warning und error gleich an; der Satz nennt den Zustand.
 */
export function meldungsIntent(message: PlainMessage, offen: boolean): 'info' | 'warning' | 'error' {
  if (message.notice === true) return 'info';
  return offen ? 'warning' : 'error';
}

export function useSectionCommands(options: SectionCommandsOptions): SectionCommands {
  const { accessToken, onReauthenticate } = options;
  const [message, setMessage] = useState<PlainMessage | undefined>(undefined);
  const [messageOffen, setMessageOffen] = useState(false);
  const melde = useCallback((satz: PlainMessage | undefined, offen = false) => {
    setMessage(satz);
    setMessageOffen(offen);
  }, []);
  const [confirmation, setConfirmation] = useState<string | undefined>(undefined);
  const [confirmationTitle, setConfirmationTitle] = useState<string | undefined>(undefined);
  const [fieldErrors, setFieldErrors] = useState<readonly FieldError[]>([]);
  const [busy, setBusy] = useState(false);
  const [submitFailed, setSubmitFailed] = useState(false);
  // Eine Funktion als Zustand braucht in useState die Huelle `() => f`.
  const [retryCommand, setRetryCommand] = useState<(() => void) | undefined>(undefined);
  const [retryLabel, setRetryLabel] = useState<string | undefined>(undefined);

  // Refs und kein Zustand: sie aendern nichts an der Darstellung, und eine
  // Wiederholung muss sie lesen, ohne auf einen Rerender zu warten.
  const heldRef = useRef<HeldRequest | undefined>(undefined);
  const refreshRef = useRef(options.refresh);
  const messagesRef = useRef(options.createMessages);
  const keepsInputRef = useRef(options.keepsInputOnConflict === true);
  // Erst nach dem Uebernehmen eines Renders, nicht waehrend: ein verworfener
  // Render duerfte sonst ein Nachladen fuer einen nicht angezeigten Stand
  // hinterlassen.
  useLayoutEffect(() => {
    refreshRef.current = options.refresh;
    messagesRef.current = options.createMessages;
    keepsInputRef.current = options.keepsInputOnConflict === true;
  });

  /** Still nachladen; was dabei schiefgeht, wird zur Meldung. */
  const nachladen = useCallback(async (nachKonflikt = false): Promise<void> => {
    try {
      const satz = await refreshRef.current(false);
      if (satz !== undefined && satz !== false) {
        melde(satz);
      }
    } catch {
      melde(nachKonflikt ? conflictNotReloadedMessage(keepsInputRef.current) : UNREACHABLE_MESSAGE);
    }
  }, [melde]);

  const statusRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLDivElement | null>(null);
  /** Der letzte Befehl endete in einer Feldablehnung; gelesen erst im Fokuseffekt. */
  const feldAbgelehnt = useRef(false);
  const [focusRequest, setFocusRequest] = useState(0);
  useEffect(() => {
    if (focusRequest > 0) {
      const feld = feldAbgelehnt.current ? erstesMarkiertesFeld(sectionRef.current) : undefined;
      feld?.focus();
      if (feld === undefined || document.activeElement !== feld) {
        statusRef.current?.focus();
      }
    }
  }, [focusRequest]);

  const execute = useCallback(
    async <Applied>(
      run: (token: string) => Promise<CommandOutcome<Applied>>,
      confirm: (applied: Applied) => string,
      onApplied: ((applied: Applied) => void) | undefined,
      reload: boolean,
      creating:
        | { readonly clearInput: () => void; readonly messages: CreateMessages | undefined; readonly refreshOnInvalid: boolean }
        | undefined,
      plain: {
        readonly confirmTitle: string | undefined;
        readonly unreachable: PlainMessage | undefined;
        readonly reloadAfterAbort?: { readonly reloaded: PlainMessage; readonly notReloaded: PlainMessage } | undefined;
        readonly retry?: () => void;
        readonly retryLabel?: string | undefined;
      },
    ): Promise<void> => {
      feldAbgelehnt.current = false;
      setBusy(true);
      melde(undefined);
      setConfirmation(undefined);
      setConfirmationTitle(undefined);
      setFieldErrors([]);
      setSubmitFailed(false);
      setRetryCommand(undefined);
      setRetryLabel(undefined);
      try {
        const token = await accessToken();
        if (token === undefined) {
          onReauthenticate();
          return;
        }
        const result = await run(token);
        if (creating !== undefined) {
          if (keepsRequest(result)) {
            setSubmitFailed(true);
          } else {
            heldRef.current = undefined;
          }
        }
        if (result.outcome === 'applied') {
          setConfirmation(confirm(result));
          setConfirmationTitle(plain.confirmTitle);
          creating?.clearInput();
          onApplied?.(result);
          if (reload) {
            await nachladen();
          }
        } else if (result.outcome === 'invalid') {
          feldAbgelehnt.current = true;
          setFieldErrors(result.errors);
          if (creating?.refreshOnInvalid === true) {
            await nachladen();
          }
        } else {
          melde(result.message);
          // Der erste Versuch ist angekommen und wurde seither geaendert: die
          // Eingabe ist erledigt und wird geleert wie nach einem Erfolg.
          if (creating !== undefined && result.outcome === 'conflict' && result.reasonCode === CLIENT_REQUEST_SUPERSEDED) {
            creating.clearInput();
          }
          if (result.outcome === 'conflict' || result.outcome === 'not-found') {
            // Nur ein Konflikt des Servers (mit Grundcode) ist sicher abgelehnt. Ein Abschnitt,
            // der einen Abbruch als Konflikt meldet, um nachzuladen, bekommt den Lesesatz.
            await nachladen(result.outcome === 'conflict' && result.reasonCode !== undefined);
          }
        }
      } catch {
        if (creating !== undefined) {
          // Die Antwort fehlt, nicht notwendig die Wirkung: die Absendekennung bleibt.
          melde((creating.messages ?? messagesRef.current)?.submitFailed ?? COMMAND_UNCERTAIN_MESSAGE, true);
          setSubmitFailed(true);
          // Im Probemodus zaehlt nur ein Erfolg; ein Scheitern aendert nichts.
          refreshRef.current(true).catch(() => undefined);
        } else if (plain.reloadAfterAbort !== undefined) {
          const saetze = plain.reloadAfterAbort;
          // Ein Satz des Nachladens (etwa eine Ablehnung) verdraengt den offenen Ausgang nicht.
          const geladen = await refreshRef.current(true).then(
            (satz) => satz === undefined,
            () => false,
          );
          // Offen bleibt offen, auch wenn das Nachladen danach scheiterte.
          melde(geladen ? saetze.reloaded : saetze.notReloaded, true);
        } else {
          melde(plain.unreachable ?? COMMAND_UNCERTAIN_MESSAGE, true);
          if (plain.retry !== undefined) {
            setRetryCommand(() => plain.retry);
            setRetryLabel(plain.retryLabel);
          }
        }
      } finally {
        setBusy(false);
        setFocusRequest((zahl) => zahl + 1);
      }
    },
    [accessToken, melde, nachladen, onReauthenticate],
  );

  const command = useCallback(
    <Applied>(spec: CommandSpec<Applied>): Promise<void> => {
      const senden = (): Promise<void> =>
        execute(spec.run, spec.confirm, spec.onApplied, spec.reload ?? true, undefined, {
          confirmTitle: spec.confirmTitle,
          unreachable: spec.unreachable,
          reloadAfterAbort: spec.reloadAfterAbort,
          ...(spec.retryAfterAbort === true ? { retry: () => void senden(), retryLabel: spec.retryLabel } : {}),
        });
      return senden();
    },
    [execute],
  );

  const create = useCallback(
    <Applied>(spec: CreateSpec<Applied>): Promise<void> => {
      const sendung = requestFor(heldRef.current, spec.input, undefined, spec.normalization ?? TRIM_ONLY);
      heldRef.current = sendung;
      return execute(
        (token) => spec.run(token, sendung.id),
        spec.confirm,
        spec.onApplied,
        true,
        { clearInput: spec.clearInput, messages: spec.messages, refreshOnInvalid: spec.refreshOnInvalid === true },
        { confirmTitle: undefined, unreachable: undefined },
      );
    },
    [execute],
  );

  const clearMessages = useCallback(() => {
    melde(undefined);
    setConfirmation(undefined);
  }, [melde]);

  const clearOutcome = useCallback(() => {
    melde(undefined);
    setConfirmation(undefined);
    setFieldErrors([]);
    setSubmitFailed(false);
    setRetryCommand(undefined);
    setRetryLabel(undefined);
  }, [melde]);

  const showConfirmation = useCallback((text: string, title: string) => {
    setConfirmation(text);
    setConfirmationTitle(title);
  }, []);

  const focusStatus = useCallback(() => {
    feldAbgelehnt.current = false;
    setFocusRequest((zahl) => zahl + 1);
  }, []);

  return {
    message,
    messageOffen,
    confirmation,
    confirmationTitle,
    fieldErrors,
    busy,
    submitFailed,
    retryCommand,
    retryLabel,
    statusRef,
    sectionRef,
    command,
    create,
    showMessage: melde,
    showConfirmation,
    clearMessages,
    clearOutcome,
    focusStatus,
  };
}
